import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';
import { siteConfig } from '../data/mock';

const PrivacyPage = () => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://summarygpt.com' },
      { '@type': 'ListItem', position: 2, name: 'Privacy', item: 'https://summarygpt.com/privacy' },
    ],
  };

  return (
    <Layout>
      <SEO
        title="Privacy Policy"
        description="SummaryGPT privacy policy. Learn how we collect, use, and protect your personal data."
        path="/privacy"
        schema={breadcrumbSchema}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow opacity-50 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-10 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>

          <div className="flex items-center gap-5 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-slate-500 text-sm">Last updated: March 20, 2023</p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Content */}
      <section className="relative py-16 md:py-20">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-slate max-w-none">
            
            {/* Introduction */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <p className="text-slate-300 leading-relaxed mb-4">
                This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
              </p>
              <p className="text-slate-300 leading-relaxed">
                We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
              </p>
            </div>

            {/* Interpretation and Definitions */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Interpretation and Definitions</h2>
              
              <h3 className="text-lg font-semibold text-amber-400 mb-3">Interpretation</h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
              </p>

              <h3 className="text-lg font-semibold text-amber-400 mb-3">Definitions</h3>
              <p className="text-slate-400 mb-4">For the purposes of this Privacy Policy:</p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Account</strong> means a unique account created for You to access our Service or parts of our Service.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to SummaryGPT.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Country</strong> refers to: New Jersey, United States</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Personal Data</strong> is any information that relates to an identified or identifiable individual.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Service</strong> refers to the Application or the Website or both.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Website</strong> refers to SummaryGPT, accessible from https://www.summarygpt.com/</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</span>
                </li>
              </ul>
            </div>

            {/* Collecting and Using Your Personal Data */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Collecting and Using Your Personal Data</h2>
              
              <h3 className="text-lg font-semibold text-amber-400 mb-3">Types of Data Collected</h3>
              
              <h4 className="text-base font-semibold text-white mb-3 mt-6">Personal Data</h4>
              <p className="text-slate-300 leading-relaxed mb-4">
                While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
              </p>
              <ul className="space-y-2 text-slate-300 mb-6">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Email address</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>First name and last name</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Phone number</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Usage Data</span>
                </li>
              </ul>

              <h4 className="text-base font-semibold text-white mb-3">Usage Data</h4>
              <p className="text-slate-300 leading-relaxed mb-4">
                Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
              </p>
              <p className="text-slate-300 leading-relaxed">
                When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.
              </p>
            </div>

            {/* Tracking Technologies and Cookies */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Tracking Technologies and Cookies</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service.
              </p>
              
              <ul className="space-y-4 text-slate-300 mb-6">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">Web Beacons.</strong> Certain sections of our Service and our emails may contain small electronic files known as web beacons that permit the Company to count users who have visited those pages or opened an email and for other related website statistics.</span>
                </li>
              </ul>

              <p className="text-slate-300 leading-relaxed mb-4">
                Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web browser.
              </p>

              <p className="text-slate-400 mb-4">We use both Session and Persistent Cookies for the purposes set out below:</p>
              
              <div className="space-y-4">
                <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                  <p className="font-semibold text-white mb-1">Necessary / Essential Cookies</p>
                  <p className="text-sm text-slate-500 mb-2">Type: Session Cookies | Administered by: Us</p>
                  <p className="text-slate-400 text-sm">These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts.</p>
                </div>
                <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                  <p className="font-semibold text-white mb-1">Cookies Policy / Notice Acceptance Cookies</p>
                  <p className="text-sm text-slate-500 mb-2">Type: Persistent Cookies | Administered by: Us</p>
                  <p className="text-slate-400 text-sm">These Cookies identify if users have accepted the use of cookies on the Website.</p>
                </div>
                <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                  <p className="font-semibold text-white mb-1">Functionality Cookies</p>
                  <p className="text-sm text-slate-500 mb-2">Type: Persistent Cookies | Administered by: Us</p>
                  <p className="text-slate-400 text-sm">These Cookies allow us to remember choices You make when You use the Website, such as remembering your login details or language preference.</p>
                </div>
              </div>
            </div>

            {/* Use of Your Personal Data */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Use of Your Personal Data</h2>
              <p className="text-slate-400 mb-4">The Company may use Personal Data for the following purposes:</p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">To provide and maintain our Service</strong>, including to monitor the usage of our Service.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">To manage Your Account:</strong> to manage Your registration as a user of the Service.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">For the performance of a contract:</strong> the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">To contact You:</strong> To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">To provide You</strong> with news, special offers and general information about other goods, services and events.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">To manage Your requests:</strong> To attend and manage Your requests to Us.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">For business transfers:</strong> We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">For other purposes:</strong> We may use Your information for data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service.</span>
                </li>
              </ul>
            </div>

            {/* Sharing Your Personal Data */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Sharing Your Personal Information</h2>
              <p className="text-slate-400 mb-4">We may share Your personal information in the following situations:</p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">With Service Providers:</strong> We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">For business transfers:</strong> We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">With Affiliates:</strong> We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">With business partners:</strong> We may share Your information with Our business partners to offer You certain products, services or promotions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-white">With Your consent:</strong> We may disclose Your personal information for any other purpose with Your consent.</span>
                </li>
              </ul>
            </div>

            {/* Retention */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Retention of Your Personal Data</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
              </p>
              <p className="text-slate-300 leading-relaxed">
                The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service.
              </p>
            </div>

            {/* Transfer */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Transfer of Your Personal Data</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer. The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy.
              </p>
            </div>

            {/* Delete Your Personal Data */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Delete Your Personal Data</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                Our Service may give You the ability to delete certain information about You from within the Service. You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information.
              </p>
              <p className="text-slate-300 leading-relaxed">
                You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us. Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so.
              </p>
            </div>

            {/* Disclosure */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Disclosure of Your Personal Data</h2>
              
              <h3 className="text-lg font-semibold text-amber-400 mb-3">Business Transactions</h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.
              </p>

              <h3 className="text-lg font-semibold text-amber-400 mb-3">Law Enforcement</h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).
              </p>

              <h3 className="text-lg font-semibold text-amber-400 mb-3">Other Legal Requirements</h3>
              <p className="text-slate-400 mb-3">The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:</p>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Comply with a legal obligation</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Protect and defend the rights or property of the Company</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Prevent or investigate possible wrongdoing in connection with the Service</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Protect the personal safety of Users of the Service or the public</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>Protect against legal liability</span>
                </li>
              </ul>
            </div>

            {/* Security */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Security of Your Personal Data</h2>
              <p className="text-slate-300 leading-relaxed">
                The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Children's Privacy</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
              </p>
              <p className="text-slate-300 leading-relaxed">
                If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.
              </p>
            </div>

            {/* Links to Other Websites */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Links to Other Websites</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.
              </p>
              <p className="text-slate-300 leading-relaxed">
                We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
              </p>
            </div>

            {/* Changes */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">Changes to this Privacy Policy</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.
              </p>
              <p className="text-slate-300 leading-relaxed">
                We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            {/* Contact Us */}
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-white mb-6">Contact Us</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, You can contact us:
              </p>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>By email: <a href="mailto:developer@summarygpt.com" className="text-amber-400 hover:text-amber-300 transition-colors">developer@summarygpt.com</a></span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>By visiting this page on our website: <a href="https://www.summarygpt.com/privacy" className="text-amber-400 hover:text-amber-300 transition-colors">https://www.summarygpt.com/privacy</a></span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPage;
